import { useQuery } from '@tanstack/react-query'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@tracker/ui'
import {
  AlertCircle,
  CheckCircle,
  Copy,
  DollarSign,
  ExternalLink,
  Filter,
  Globe,
  Search,
  XCircle,
} from 'lucide-react'
import { useState } from 'react'
import { type Campaign, publisherApi } from '../lib/api'

export function Campaigns() {
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'paused'>('all')
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const { data: campaigns = [], isLoading } = useQuery({
    queryKey: ['campaigns'],
    queryFn: publisherApi.getCampaigns,
  })

  const filteredCampaigns = campaigns.filter((campaign) => {
    const matchesSearch =
      campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.description?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = filterStatus === 'all' || campaign.status === filterStatus
    return matchesSearch && matchesStatus
  })

  const copyTrackingLink = (campaign: Campaign) => {
    navigator.clipboard.writeText(campaign.trackingUrl)
    setCopiedId(campaign.id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const getPayoutTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      cpa: 'Cost Per Action',
      cpl: 'Cost Per Lead',
      cps: 'Cost Per Sale',
      revshare: 'Revenue Share',
    }
    return labels[type] || type.toUpperCase()
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Available Campaigns</h1>
        <p className="text-gray-600 mt-1">
          Choose campaigns to promote and get your tracking links
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search campaigns..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 text-muted-foreground" />
              <Select
                value={filterStatus}
                onValueChange={(value) => setFilterStatus(value as 'all' | 'active' | 'paused')}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter campaigns" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Campaigns</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Campaign Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredCampaigns.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="p-12 text-center">
              <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold">No campaigns found</h3>
              <p className="text-muted-foreground mt-2">
                Try adjusting your search or filter criteria
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredCampaigns.map((campaign) => (
            <Card key={campaign.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="line-clamp-1">{campaign.name}</CardTitle>
                    <CardDescription className="mt-1 line-clamp-2">
                      {campaign.description || 'No description available'}
                    </CardDescription>
                  </div>
                  <Badge
                    variant={campaign.status === 'active' ? 'success' : 'secondary'}
                    className="ml-2"
                  >
                    {campaign.status === 'active' ? (
                      <div className="flex items-center">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Active
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <XCircle className="w-3 h-3 mr-1" />
                        Paused
                      </div>
                    )}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                {/* Campaign Details */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm text-muted-foreground">
                      <DollarSign className="w-4 h-4 mr-1" />
                      <span>Payout</span>
                    </div>
                    <div className="text-sm font-semibold">
                      ${campaign.payout.toFixed(2)} / {getPayoutTypeLabel(campaign.payoutType)}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm text-muted-foreground">
                      <Globe className="w-4 h-4 mr-1" />
                      <span>Countries</span>
                    </div>
                    <div className="text-sm">
                      {campaign.countries.length > 0
                        ? campaign.countries.join(', ')
                        : 'All Countries'}
                    </div>
                  </div>
                </div>

                {/* Tracking Link */}
                <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-2">Tracking Link</p>
                  <div className="flex items-center space-x-2">
                    <Input
                      type="text"
                      value={campaign.trackingUrl}
                      readOnly
                      className="flex-1 text-xs font-mono"
                    />
                    <Button
                      size="sm"
                      variant={copiedId === campaign.id ? 'default' : 'outline'}
                      onClick={() => copyTrackingLink(campaign)}
                      title="Copy tracking link"
                      className={copiedId === campaign.id ? 'bg-green-600 hover:bg-green-700' : ''}
                    >
                      {copiedId === campaign.id ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>

              <CardFooter className="bg-muted/50">
                <div className="flex items-center justify-between w-full">
                  <Button variant="link" size="sm" asChild>
                    <a
                      href={campaign.previewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center"
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Preview Landing Page
                    </a>
                  </Button>
                  <span className="text-xs text-muted-foreground">{campaign.category}</span>
                </div>
              </CardFooter>
            </Card>
          ))
        )}
      </div>

      {/* Stats Summary */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-muted-foreground">Total Campaigns</p>
              <p className="text-2xl font-bold">{campaigns.length}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Active Campaigns</p>
              <p className="text-2xl font-bold">
                {campaigns.filter((c) => c.status === 'active').length}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Average Payout</p>
              <p className="text-2xl font-bold">
                $
                {campaigns.length > 0
                  ? (campaigns.reduce((sum, c) => sum + c.payout, 0) / campaigns.length).toFixed(2)
                  : '0.00'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
